import React, { useEffect, useState } from "react";
import { Card, Col, Row, Select, Table } from "antd";
import { useTranslation } from "react-i18next";
import { useComplaints } from "../../../../data/hooks/firestore/useComplaints";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { COMPLAINT_REASONS, DEPARTMENTS } from "../../../../constants/DepartmentConstants";
import dayjs from "dayjs";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { formatPrice } from "../../../../functions/formatters";
import utils from "../../../../utils";
import DonutChart from "../../../../components/shared-components/DonutChart";
import { COLOR_9, COLORS } from "../../../../constants/ChartConstant";
import DataDisplayWidget from "../../../../components/shared-components/DataDisplayWidget";
import { AlertOutlined, DollarOutlined, FileTextOutlined, ShoppingOutlined } from "@ant-design/icons";

const { Option } = Select;

const ComplaintsReport = ({ dateRange, dateRangeText, loading: parentLoading, companyFilter }) => {
  const { t } = useTranslation();
  const { data: complaints, loading: complaintsLoading } = useComplaints(30, dateRange);
  const { customers, isLoading: customersLoading } = useCustomers();
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [reasonFilter, setReasonFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [stats, setStats] = useState({
    totalComplaints: 0,
    totalProducts: 0,
    totalCreditAmount: 0,
    mostCommonReason: "",
  });
  const [reasonsBreakdown, setReasonsBreakdown] = useState([]);
  const [departmentsBreakdown, setDepartmentsBreakdown] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);

  const loading = parentLoading || complaintsLoading || customersLoading;

  // Get translated labels
  const getReasonLabel = (reasonId) => {
    const reason = COMPLAINT_REASONS.find((r) => r.id === reasonId);
    return reason ? t(reason.labelKey) : reasonId;
  };

  const getDepartmentLabel = (deptId) => {
    const dept = DEPARTMENTS.find((d) => d.id === deptId);
    return dept ? t(dept.nameKey) : deptId;
  };

  // Filter complaints by date range
  useEffect(() => {
    if (complaints && dateRange && customers) {
      const start = dayjs(dateRange[0]).startOf("day").unix();
      const end = dayjs(dateRange[1]).endOf("day").unix();

      let filtered = complaints.filter((complaint) => {
        const complaintDate = complaint.createdAt;
        return complaintDate >= start && complaintDate <= end;
      });

      // Apply company filter
      if (companyFilter !== "All") {
        filtered = filtered.filter((complaint) => {
          // Use new companyCode field, fallback to checking customer for old complaints
          if (complaint.companyCode) {
            return complaint.companyCode === companyFilter;
          }
          // Fallback for old complaints without companyCode
          if (!complaint.farmorderCustomerId) return false;
          const customer = customers.find((c) => c.id === complaint.farmorderCustomerId);
          return customer?.company === companyFilter;
        });
      }
      // When companyFilter === "All", show all complaints including those without customer

      setFilteredComplaints(filtered);
    }
  }, [complaints, dateRange, customers, companyFilter]);

  // Calculate stats and breakdowns
  useEffect(() => {
    if (filteredComplaints.length > 0) {
      let totalProducts = 0;
      let totalAmount = 0;
      const reasonsMap = {};
      const departmentsMap = {};
      const customersMap = {};

      filteredComplaints.forEach((complaint) => {
        // Handle complaints with products
        if (complaint.products && Array.isArray(complaint.products) && complaint.products.length > 0) {
          complaint.products.forEach((product) => {
            const quantity = product.quantity || 0;
            const amount = product.totalAmount || 0;
            const reason = product.reason || "unknown";
            const department = product.department || "unknown";

            totalProducts += quantity;
            totalAmount += amount;

            // Aggregate by reason
            if (reasonsMap[reason]) {
              reasonsMap[reason].count += quantity;
              reasonsMap[reason].amount += amount;
            } else {
              reasonsMap[reason] = { reason, count: quantity, amount };
            }

            // Aggregate by department
            if (departmentsMap[department]) {
              departmentsMap[department].count += quantity;
              departmentsMap[department].amount += amount;
            } else {
              departmentsMap[department] = { department, count: quantity, amount };
            }
          });
        } else {
          // Handle general complaints (no products)
          // Use top-level fields for aggregation
          const reason = complaint.reason || "unknown";
          const department = complaint.department || "unknown";

          // Aggregate by reason (count as 1 complaint)
          if (reasonsMap[reason]) {
            reasonsMap[reason].count += 1;
          } else {
            reasonsMap[reason] = { reason, count: 1, amount: 0 };
          }

          // Aggregate by department (count as 1 complaint)
          if (departmentsMap[department]) {
            departmentsMap[department].count += 1;
          } else {
            departmentsMap[department] = { department, count: 1, amount: 0 };
          }
        }

        // Aggregate by customer (skip if no customer linked)
        const customerId = complaint.farmorderId || null;
        const customerName = complaint.company || t("no_customer_linked");
        const complaintAmount =
          complaint.products && complaint.products.length > 0
            ? complaint.products.reduce((sum, p) => sum + (p.totalAmount || 0), 0)
            : 0;

        // Only aggregate if customer is linked
        if (customerId) {
          if (customersMap[customerId]) {
            customersMap[customerId].complaintCount += 1;
            customersMap[customerId].amount += complaintAmount;
          } else {
            customersMap[customerId] = {
              customerId,
              customerName,
              complaintCount: 1,
              amount: complaintAmount,
            };
          }
        }
      });

      // Find most common reason
      const reasonsArray = Object.values(reasonsMap).sort((a, b) => b.count - a.count);
      const mostCommonReason = reasonsArray.length > 0 ? getReasonLabel(reasonsArray[0].reason) : "-";

      setStats({
        totalComplaints: filteredComplaints.length,
        totalProducts,
        totalCreditAmount: totalAmount,
        mostCommonReason,
      });

      setReasonsBreakdown(reasonsArray);
      setDepartmentsBreakdown(Object.values(departmentsMap).sort((a, b) => b.count - a.count));
      setTopCustomers(
        Object.values(customersMap)
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5),
      );
    } else {
      setStats({
        totalComplaints: 0,
        totalProducts: 0,
        totalCreditAmount: 0,
        mostCommonReason: "-",
      });
      setReasonsBreakdown([]);
      setDepartmentsBreakdown([]);
      setTopCustomers([]);
    }
  }, [filteredComplaints]);

  // Prepare table data with filters
  useEffect(() => {
    if (filteredComplaints.length > 0) {
      const rows = [];

      filteredComplaints.forEach((complaint) => {
        if (complaint.products && Array.isArray(complaint.products) && complaint.products.length > 0) {
          // Regular complaints with products
          complaint.products.forEach((product, index) => {
            // Apply filters
            if (reasonFilter !== "all" && product.reason !== reasonFilter) return;
            if (departmentFilter !== "all" && product.department !== departmentFilter) return;

            rows.push({
              key: `${complaint.id}-${index}`,
              complaintId: complaint.id,
              date: complaint.createdAt,
              customerName: complaint.company || "-",
              farmorderId: complaint.farmorderId || "-",
              productName: product.product?.productName || "-",
              quantity: product.quantity || 0,
              reason: product.reason,
              customReason: product.customReason,
              department: product.department,
              creditAmount: product.totalAmount || 0,
              createdBy: complaint.createdBy,
            });
          });
        } else {
          // General complaints (no products)
          // Apply filters on top-level fields
          if (reasonFilter !== "all" && complaint.reason !== reasonFilter) return;
          if (departmentFilter !== "all" && complaint.department !== departmentFilter) return;

          rows.push({
            key: complaint.id,
            complaintId: complaint.id,
            date: complaint.createdAt,
            customerName: complaint.company || "-",
            farmorderId: complaint.farmorderId || "-",
            productName: t("general_complaint"),
            quantity: 0,
            reason: complaint.reason,
            customReason: complaint.customReason,
            department: complaint.department,
            creditAmount: 0,
            createdBy: complaint.createdBy,
          });
        }
      });

      setTableData(rows);
    } else {
      setTableData([]);
    }
  }, [filteredComplaints, reasonFilter, departmentFilter]);

  const tableColumns = [
    {
      title: t("date"),
      dataIndex: "date",
      render: (timestamp) => dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm"),
      sorter: (a, b) => a.date - b.date,
      width: 150,
    },
    {
      title: t("customer.name"),
      dataIndex: "customerName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "customerName"),
    },
    {
      title: t("order_id"),
      dataIndex: "farmorderId",
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderId"),
      width: 120,
    },
    {
      title: t("product_name"),
      dataIndex: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      sorter: (a, b) => utils.antdTableSorter(a, b, "quantity"),
      width: 100,
    },
    {
      title: t("complaint_reason"),
      dataIndex: "reason",
      render: (reason, record) => (
        <span>
          {getReasonLabel(reason)}
          {record.customReason && <div style={{ fontSize: "11px", color: "#999" }}>{record.customReason}</div>}
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "reason"),
    },
    {
      title: t("department_error"),
      dataIndex: "department",
      render: (dept) => getDepartmentLabel(dept),
      sorter: (a, b) => utils.antdTableSorter(a, b, "department"),
    },
    {
      title: t("credit_amount"),
      dataIndex: "creditAmount",
      render: (amount) => <span className="font-weight-semibold">{CURRENCY + formatPrice(amount)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "creditAmount"),
      width: 120,
    },
    {
      title: t("created_by"),
      dataIndex: "createdBy",
      sorter: (a, b) => utils.antdTableSorter(a, b, "createdBy"),
    },
  ];

  return (
    <>
      {/* Overview Stats */}
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <DataDisplayWidget
            icon={<FileTextOutlined />}
            value={stats.totalComplaints}
            title={t("total_complaints")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DataDisplayWidget
            icon={<ShoppingOutlined />}
            value={stats.totalProducts}
            title={t("products_credited")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DataDisplayWidget
            icon={<DollarOutlined />}
            value={CURRENCY + formatPrice(stats.totalCreditAmount)}
            title={t("total_credit_amount")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DataDisplayWidget
            icon={<AlertOutlined />}
            value={stats.mostCommonReason}
            title={t("most_common_reason")}
            color={COLOR_9}
            vertical={true}
            avatarSize={55}
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title={t("complaints_by_reason")}>
            {reasonsBreakdown.length > 0 ? (
              <DonutChart
                series={reasonsBreakdown.map((r) => r.count)}
                labels={reasonsBreakdown.map((r) => getReasonLabel(r.reason))}
                colors={COLORS}
                height={300}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <span className="text-muted">{t("no_data_available")}</span>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("complaints_by_department")}>
            {departmentsBreakdown.length > 0 ? (
              <DonutChart
                series={departmentsBreakdown.map((d) => d.count)}
                labels={departmentsBreakdown.map((d) => getDepartmentLabel(d.department))}
                colors={COLORS}
                height={300}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <span className="text-muted">{t("no_data_available")}</span>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Top Customers */}
      <Row gutter={16}>
        <Col xs={24}>
          <Card title={t("top_customers_with_complaints")}>
            {topCustomers.length > 0 ? (
              <Table
                showSorterTooltip={false}
                columns={[
                  {
                    title: t("customer.name"),
                    dataIndex: "customerName",
                    sorter: (a, b) => utils.antdTableSorter(a, b, "customerName"),
                  },
                  {
                    title: t("complaint_count"),
                    dataIndex: "complaintCount",
                    sorter: (a, b) => utils.antdTableSorter(a, b, "complaintCount"),
                  },
                  {
                    title: t("total_credit_amount"),
                    dataIndex: "amount",
                    render: (amount) => <span className="font-weight-semibold">{CURRENCY + formatPrice(amount)}</span>,
                    sorter: (a, b) => utils.antdTableSorter(a, b, "amount"),
                  },
                ]}
                dataSource={topCustomers}
                rowKey="customerId"
                pagination={false}
                scroll={{ x: 400 }}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <span className="text-muted">{t("no_data_available")}</span>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Detail Table with Filters */}
      <Card title={t("complaint_details")}>
        <div style={{ marginBottom: 16 }}>
          <span className="text-muted">{dateRangeText}</span>
        </div>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("filter_by_reason")}
              value={reasonFilter}
              onChange={setReasonFilter}
            >
              <Option value="all">{t("all_reasons")}</Option>
              {COMPLAINT_REASONS.map((reason) => (
                <Option key={reason.id} value={reason.id}>
                  {t(reason.labelKey)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("filter_by_department")}
              value={departmentFilter}
              onChange={setDepartmentFilter}
            >
              <Option value="all">{t("all_departments")}</Option>
              {DEPARTMENTS.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {t(dept.nameKey)}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={tableColumns}
            dataSource={tableData}
            rowKey="key"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 50 }}
            scroll={{ x: 1200 }}
          />
        </div>
      </Card>
    </>
  );
};

export default ComplaintsReport;
